/** Session stash for Zernio headless OAuth return — survives blank first paint / remounts. */

export const GBP_OAUTH_RESUME_KEY = 'gbp_oauth_resume';
export const GBP_RETURNING_FLAG = 'gbp_returning';

export type GbpOauthResume = {
  businessId: string;
  profileId: string;
  pendingDataToken: string;
  connectToken: string | null;
  savedAt: number;
};

export function stashGbpOauthResume(resume: Omit<GbpOauthResume, 'savedAt'>): void {
  try {
    const payload: GbpOauthResume = { ...resume, savedAt: Date.now() };
    sessionStorage.setItem(GBP_OAUTH_RESUME_KEY, JSON.stringify(payload));
    sessionStorage.setItem(GBP_RETURNING_FLAG, '1');
  } catch {
    /* ignore */
  }
}

export function peekGbpOauthResume(): GbpOauthResume | null {
  try {
    const raw = sessionStorage.getItem(GBP_OAUTH_RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GbpOauthResume;
    if (!parsed?.pendingDataToken || !parsed?.profileId) return null;
    // Expire after 30 minutes
    if (parsed.savedAt && Date.now() - parsed.savedAt > 30 * 60 * 1000) {
      clearGbpOauthResume();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearGbpOauthResume(): void {
  try {
    sessionStorage.removeItem(GBP_OAUTH_RESUME_KEY);
    sessionStorage.removeItem(GBP_RETURNING_FLAG);
  } catch {
    /* ignore */
  }
}

/** Drop Google Business OAuth query params, leaving the rest of the URL alone. */
export function stripGbpOauthSearch(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of [
    'gbp_oauth',
    'gbp_returning',
    'gbp_error',
    'gbp_error_message',
    'gbp_is_user_fixable',
    'pendingDataToken',
    'pending_data_token',
    'profileId',
    'profile_id',
    'connect_token',
    'connectToken',
    'feedback_connect',
  ]) {
    params.delete(key);
  }
  if ((params.get('success') || '').startsWith('google_business_')) params.delete('success');
  if ((params.get('error') || '').startsWith('google_business_')) {
    params.delete('error');
    params.delete('error_message');
    params.delete('errorMessage');
    params.delete('is_user_fixable');
    params.delete('isUserFixable');
  }
  if ((params.get('platform') || '').toLowerCase().replace(/[_-]/g, '') === 'googlebusiness') {
    params.delete('platform');
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function isGbpOauthReturning(): boolean {
  try {
    if (sessionStorage.getItem(GBP_RETURNING_FLAG) === '1') return true;
    if (sessionStorage.getItem(GBP_OAUTH_RESUME_KEY)) return true;
  } catch {
    /* ignore */
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('gbp_returning') === '1' ||
      !!params.get('pendingDataToken') ||
      !!params.get('pending_data_token')
    );
  } catch {
    return false;
  }
}
